import { useState, useEffect } from 'react'
import { supabase } from './lib/supabase'
import { offlineDB } from './lib/db'
import AssessmentForm from './components/AssessmentForm'
import RiskSummary from './components/RiskSummary'
import OfflineIndicator from './components/OfflineIndicator'

export default function App() {
  const [assessments, setAssessments] = useState<Record<string, any>[]>([])
  const [currentAssessment, setCurrentAssessment] = useState<Record<string, any> | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    initApp()
  }, [])

  async function initApp() {
    try {
      await offlineDB.init()
      const localAssessments = await offlineDB.getAllAssessments()
      setAssessments(localAssessments)
      
      if (navigator.onLine) {
        const { data } = await supabase.from('assessments').select()
        if (data) {
          setAssessments(data)
        }
      }
    } catch (error) {
      console.error('Failed to init app:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAssessment = async (formData: Record<string, any>) => {
    try {
      console.log('formData received:', formData)
      
      const localId = 'local_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
      console.log('Generated localId:', localId)
      
      const assessmentForLocal = {
        id: localId,
        site_name: formData.site_name || 'Untitled',
        client_name: formData.client_name || '',
        survey_date: formData.survey_date || '',
        assessor_name: formData.assessor_name || '',
        status: 'draft',
      }
      
      console.log('assessmentForLocal:', assessmentForLocal)
      
      const assessmentForSupabase = {
        site_name: formData.site_name || 'Untitled',
        client_name: formData.client_name || '',
        survey_date: formData.survey_date || '',
        assessor_name: formData.assessor_name || '',
        status: 'draft',
      }
      
      console.log('Saving to IndexedDB...')
      await offlineDB.saveAssessment(assessmentForLocal)
      console.log('IndexedDB save successful!')
      
      if (navigator.onLine) {
        try {
          console.log('Sending to Supabase...')
          await supabase.from('assessments').insert([assessmentForSupabase]).select()
          console.log('Supabase insert successful!')
        } catch (syncError) {
          console.log('Supabase sync error:', syncError)
        }
      }

      setCurrentAssessment(null)
      initApp()
    } catch (error) {
      console.error('Failed to save assessment:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-irrigonics-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading LRA Assessment...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-4">
        <header className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Legionella Risk Assessment</h1>
          <p className="text-gray-600">Irrigonics Ltd - Water Hygiene & Compliance</p>
        </header>

        {!currentAssessment ? (
          <div className="space-y-4">
            <button
              onClick={() => setCurrentAssessment({})}
              className="w-full bg-irrigonics-600 hover:bg-irrigonics-700 text-white font-bold py-3 px-4 rounded-lg transition"
            >
              + New Assessment
            </button>

            {assessments.length > 0 && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Recent Assessments</h2>
                  <div className="space-y-2">
                    {assessments.map(a => (
                      <button
                        key={a.id}
                        onClick={() => setCurrentAssessment(a)}
                        className="w-full text-left p-3 border rounded hover:bg-gray-50 transition"
                      >
                        <div className="font-medium">{a.site_name}</div>
                        <div className="text-sm text-gray-600">{a.survey_date} • {a.status}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <AssessmentForm
              assessment={currentAssessment}
              onSave={handleSaveAssessment}
              onCancel={() => setCurrentAssessment(null)}
            />
            {currentAssessment.form_data && (
              <RiskSummary formData={currentAssessment.form_data} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
